import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguageData } from '../contexts/LanguageContext';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import PracticeModal from './PracticeModal';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import { mapLevel } from '../utils/languageUtils';
import './Common.css';
import './ExploreLearning.css';
import './Home.css';

const ExploreLearning = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language: initialLanguage = 'de', proficiencyLevel: initialLevel = 'beginner-a1' } = location.state || {};
  
  console.log('ExploreLearning - location.state:', location.state);
  console.log('ExploreLearning - initialLanguage:', initialLanguage);
  console.log('ExploreLearning - initialLevel:', initialLevel);
  
  const [language, setLanguage] = React.useState(initialLanguage);
  const [proficiencyLevel, setProficiencyLevel] = React.useState(initialLevel);
  const [isPracticeModalOpen, setIsPracticeModalOpen] = React.useState(false);
  const [showPostAssessmentPopup, setShowPostAssessmentPopup] = useState(false);
  const [postAssessmentConfirmed, setPostAssessmentConfirmed] = useState(false);
  const [showPopupLangDropdown, setShowPopupLangDropdown] = useState(false);
  const [popupLanguage, setPopupLanguage] = useState(language);
  const [examAttemptsLeft, setExamAttemptsLeft] = useState(2);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

  // Fetch remaining Exam Prep attempts (per language + level, resets every 24h).
  // Runs on landing and whenever language/level changes (via PageHeader dropdowns).
  // Uses a cache-busting param + no-cache headers so the browser never serves a
  // stale count without clearing cache.
  React.useEffect(() => {
    const lvl = mapLevel(proficiencyLevel);
    // Japanese exam attempts live in a separate table (jlpt_exam_attempts), so use
    // the dedicated endpoint; all other languages use the standard one.
    const isJapanese = language === 'ja';
    const url = isJapanese
      ? `${API_BASE}/japanese-exam/check-attempts?level=${lvl}&_t=${Date.now()}`
      : `${API_BASE}/exam-prep/check-attempts?language_code=${language}&level=${lvl}&_t=${Date.now()}`;
    console.log('[exam-prep attempts] requesting:', url);
    axios.get(url, {
      withCredentials: true,
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
    })
      .then(res => {
        console.log('[exam-prep attempts] response:', res.data);
        if (res.data && res.data.success && typeof res.data.attempts_remaining === 'number') {
          setExamAttemptsLeft(res.data.attempts_remaining);
        }
      })
      .catch(err => { console.warn('[exam-prep attempts] error:', err?.response?.status, err?.message); });
  }, [language, proficiencyLevel, API_BASE]);

  const fallbackLanguages = [
    { code: 'en', name: 'English', flag: '/images/flag-english.svg', active: true },
    { code: 'fr', name: 'French', flag: '/images/flag-french.svg', active: true },
    { code: 'de', name: 'German', flag: '/images/flag-german.svg', active: true },
    { code: 'es', name: 'Spanish', flag: '/images/flag-spanish.svg', active: true },
    { code: 'it', name: 'Italian', flag: '/images/flag-italian.svg', active: true },
    { code: 'ja', name: 'Japanese', flag: '/images/flag-japanese.svg', active: true }
  ];

  const [langList, setLangList] = useState(fallbackLanguages);

  React.useEffect(() => {
    axios.get(API_BASE + '/languages', { withCredentials: true })
      .then(function(res) {
        if (res.data && typeof res.data === 'object') {
          var langs = Object.entries(res.data).map(function(entry) {
            return { code: entry[0], name: entry[1].label, active: entry[1].active !== false && entry[1].active !== 0, flag: entry[1].flag || '/images/flag-' + entry[1].label.toLowerCase() + '.svg' };
          });
          if (langs.length > 0) setLangList(langs);
        }
      }).catch(function() {});
  }, [API_BASE]);

  const selectedPopupLang = langList.find(function(l) { return l.code === popupLanguage; });

  const handleStartPostAssessment = function() {
    setShowPostAssessmentPopup(false);
    setPostAssessmentConfirmed(false);
    navigate('/post-assessment', { state: { language: popupLanguage, proficiencyLevel: proficiencyLevel, freshStart: true } });
  };

  usePageTimeTracker('explore-learning', user);
  
  // Use shared language data from context
  const { loading, languages: languagesData, proficiencyLevels: levelsData } = useLanguageData();
  
  console.log('ExploreLearning - languagesData:', languagesData);
  console.log('ExploreLearning - levelsData:', levelsData);
  console.log('ExploreLearning - current language:', language);
  console.log('ExploreLearning - current level:', proficiencyLevel);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', current: true }
  ];

  return (
    <div className="explore-learning">
      <Header user={user} />
      
      <div className="nav-header-container">
        <SideNav />
        <PageHeader 
          breadcrumbs={breadcrumbs}
          language={language}
          proficiencyLevel={proficiencyLevel}
          onLanguageChange={setLanguage}
          onLevelChange={setProficiencyLevel}
        />
      </div>
      
      <main className="explore-content">
        <h1 className="page-title">
          Explore the next step in your language learning journey 
        </h1>

        <div className="learning-sections">
          <div className="learning-card">
            <div className="card-illustration">
              <img src="/images/card-learn.png" alt="Learn" />
            </div>
            <h3 className="card-title">Learn</h3>
            <p className="card-description">
              Master core language concepts and usage step-by-step.
            </p>
           
            <div className="card-extra-links">
              <a 
                href="#go-to-learn" 
                className="extra-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/learn', { state: { language, proficiencyLevel } });
                }}
              >
                Go to learn
                <img src="/images/Arrow.svg" alt="" className="arrow-icon" />
              </a>
            </div>
          </div>

          <div className="learning-card">
            <div className="card-illustration">
              <img src="/images/card-practice.png" alt="Practice" />
            </div>
            <h3 className="card-title">Practice</h3>
            <p className="card-description">
              Reinforce learning through hands-on<br />language exercises.
            </p>
            <div className="card-extra-links">
              <a 
                href="#language-skills" 
                className="extra-link"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPracticeModalOpen(true);
                }}
              >
                Language Skills
                <img src="/images/Arrow.svg" alt="" className="arrow-icon" />
              </a>
              <a 
                href="#business-scenario" 
                className="extra-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/practice-business', { state: { language, proficiencyLevel } });
                }}
              >
                Business Scenarios
                <img src="/images/Arrow.svg" alt="" className="arrow-icon" />
              </a>
            </div>
          </div>

          <div className="learning-card">
              <div className="card-illustration">
                <img src="/images/assessment3rdimage.png" alt="Assessment" />
              </div>
              <h3 className="card-title">Assessment</h3>
              <p className="card-description">
                Track growth and performance <br />milestones for each level.
              </p>
              <div className="card-extra-links">
                <a className="extra-link" onClick={function() { setPopupLanguage(language); setShowPostAssessmentPopup(true); }}>
                  Post Assessment
                  <img src="/images/Arrow.svg" alt="" className="arrow-icon" />
                </a>
                {(() => {
                  // Japanese has its own dedicated Exam Prep page (different flow /
                  // exam structure); all other languages use the standard page.
                  const isJapanese = language === 'ja';
                  const disabled = examAttemptsLeft <= 0;
                  const examPath = isJapanese ? '/japanese-exam-prep' : '/exam-prep';
                  return (
                    <>
                      <a
                        className={'extra-link' + (disabled ? ' coming-soon-link' : '')}
                        onClick={(e) => { e.preventDefault(); if (!disabled) navigate(examPath, { state: { language, proficiencyLevel } }); }}
                      >
                        Exam Prep
                        <img src="/images/Arrow.svg" alt="" className="arrow-icon" />
                      </a>
                      <span className={'exam-prep-attempts' + (disabled ? ' none' : '')}>
                        {examAttemptsLeft >= 2
                          ? '2 attempts every 24 hours'
                          : examAttemptsLeft === 1
                            ? 'You are left with 1 attempt for today'
                            : "You've used all your attempts for today. Please try again tomorrow."}
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>

          <div className="learning-card">
            <div className="card-illustration">
              <img src="/images/activeculturalinsights.svg" alt="Cultural Insights" />
            </div>
            <h3 className="card-title">Cultural Insights</h3>
            <p className="card-description">
              Explore the culture and context<br />behind a language.
            </p>
            <div className="card-extra-links">
              <a className="extra-link" onClick={() => navigate('/cultural-insights', { state: { language, proficiencyLevel } })}>
                Know more <img src="/images/Arrow.svg" alt="" width="16" height="16" />
              </a>
              <a className="extra-link" onClick={() => navigate('/cultural-insights-challenge', { state: { language, proficiencyLevel } })}>
                Cultural Insight Challenge <img src="/images/Arrow.svg" alt="" width="16" height="16" />
              </a>
            </div>

          </div>

          <div className="card-info-tip-outside">
            <img src="/images/purpleInfo.svg" alt="" width="18" height="18" />
            <span>Attempt Post Assessment after covering recommended Learn and Practice sessions for best results.</span>
          </div>
        </div>
      </main>

      <Footer />
      
      <PracticeModal 
        isOpen={isPracticeModalOpen}
        onClose={() => setIsPracticeModalOpen(false)}
        language={language}
        proficiencyLevel={proficiencyLevel}
      />

      {showPostAssessmentPopup && (
        <div className="home-new-popup-overlay">
          <div className="home-new-popup">
            <div className="home-new-popup-header">
              <span>Please read the following before beginning your Post Assessment</span>
              <button className="home-new-popup-close" onClick={function() { setShowPostAssessmentPopup(false); setPostAssessmentConfirmed(false); }}>&times;</button>
            </div>
            <div className="home-new-popup-items">
              <div className="home-new-popup-item"><img src="/images/Star-black.svg" alt="" width="20" height="20" /><div><h4>Complete this before you start learning:</h4><p>A quick language check to build an honest picture of your level. It&#8217;s fine if you&#8217;re unsure&#8212;give it your best answer.</p></div></div>
              <div className="home-new-popup-item"><img src="/images/Chart-black.svg" alt="" width="20" height="20" /><div><h4>Questions adapt as you go:</h4><p>The difficulty adjusts based on your answers&#8212;some questions may feel easier or harder than expected.</p></div></div>
              <div className="home-new-popup-item"><img src="/images/black-info.svg" alt="" width="20" height="20" /><div><h4>Don&#8217;t exit before submitting:</h4><p>Closing or navigating away before you finish will reset the assessment. You&#8217;ll need to start over.</p></div></div>
              <div className="home-new-popup-item"><img src="/images/time-black.svg" alt="" width="20" height="20" /><div><h4>Session Timeout?</h4><p>Your answers are safe if your session expires, all completed responses are automatically submitted - Nothing is lost.</p></div></div>
            </div>
            <div className="home-new-popup-divider"></div>
            <div className="home-new-popup-lang">
              <div className="home-new-popup-lang-box">
                <button className="home-new-popup-lang-btn" onClick={function() { setShowPopupLangDropdown(!showPopupLangDropdown); }}>
                  <span className="home-new-popup-lang-name">{selectedPopupLang ? selectedPopupLang.name : ''} |</span>
                  <span className="home-new-popup-lang-change">Change Language</span>
                  <img src="/images/purplearrow.svg" alt="" width="16" height="16" />
                </button>
                {showPopupLangDropdown && (
                  <div className="home-new-popup-lang-dropdown">
                    {langList.map(function(l) { return (<button key={l.code} className={'home-new-popup-lang-item' + (!l.active ? ' inactive' : '')} onClick={function() { if (l.active) { setPopupLanguage(l.code); setShowPopupLangDropdown(false); } }}><img src={l.flag} alt="" width="20" height="14" />{l.name}</button>); })}
                  </div>
                )}
                <div className="home-new-popup-lang-info"><img src="/images/black-info.svg" alt="" width="16" height="16" /><span>You have already selected {selectedPopupLang ? selectedPopupLang.name : ''}, you can continue or change language here</span></div>
              </div>
            </div>
            <label className="home-new-popup-checkbox"><input type="checkbox" checked={postAssessmentConfirmed} onChange={function(e) { setPostAssessmentConfirmed(e.target.checked); }} /><span>I understand my language selection cannot be changed once the challenge begins</span></label>
            <div className="home-new-popup-footer">
              <button className="home-new-popup-start" onClick={handleStartPostAssessment} disabled={!postAssessmentConfirmed}>Start Post Assessment</button>
              <span className="home-new-popup-hint">Take your time and trust your instincts.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreLearning;